import { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Eraser, Maximize2, Check, X } from "lucide-react";

// Canvas signature pad with an optional touch-friendly fullscreen mode for field deliveries.
// Calls onChange(dataUrl) on stroke end, onChange("") on clear.
export function SignaturePad({ value, onChange, height = 160 }) {
  const canvasRef = useRef(null);
  const fsCanvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(!value);
  const [fs, setFs] = useState(false);

  const paint = useCallback((canvas, dataUrl) => {
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2.6;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    if (dataUrl) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = dataUrl;
    }
  }, []);

  useEffect(() => {
    paint(canvasRef.current, value);
    setEmpty(!value);
  }, []); // eslint-disable-line

  const pos = (canvas, e) => {
    const r = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (canvas.width / r.width), y: (t.clientY - r.top) * (canvas.height / r.height) };
  };
  const bind = (ref) => ({
    onMouseDown: (e) => start(ref.current, e), onMouseMove: (e) => move(ref.current, e), onMouseUp: () => end(ref.current), onMouseLeave: () => end(ref.current),
    onTouchStart: (e) => start(ref.current, e), onTouchMove: (e) => move(ref.current, e), onTouchEnd: () => end(ref.current),
  });
  const start = (canvas, e) => { e.preventDefault(); drawing.current = true; const ctx = canvas.getContext("2d"); const p = pos(canvas, e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (canvas, e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvas.getContext("2d"); const p = pos(canvas, e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = (canvas) => { if (!drawing.current) return; drawing.current = false; setEmpty(false); onChange && onChange(canvas.toDataURL("image/png")); };
  const clearCanvas = (canvas) => { canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height); const c = canvas.getContext("2d"); c.lineWidth = 2.6; c.lineJoin = "round"; c.lineCap = "round"; c.strokeStyle = "#0f172a"; };
  const clear = () => { clearCanvas(canvasRef.current); if (fs && fsCanvasRef.current) clearCanvas(fsCanvasRef.current); setEmpty(true); onChange && onChange(""); };

  const openFs = () => { setFs(true); };
  useEffect(() => {
    if (fs && fsCanvasRef.current) { paint(fsCanvasRef.current, value); }
  }, [fs]); // eslint-disable-line
  const validateFs = () => {
    const data = empty ? "" : (fsCanvasRef.current ? fsCanvasRef.current.toDataURL("image/png") : value);
    if (data) { paint(canvasRef.current, data); setEmpty(false); onChange && onChange(data); }
    setFs(false);
  };

  return (
    <div>
      <div className="rounded-lg border-2 border-dashed border-slate-300 bg-white relative overflow-hidden">
        <canvas
          ref={canvasRef} width={600} height={height} data-testid="signature-canvas"
          className="w-full touch-none cursor-crosshair" style={{ height }}
          {...bind(canvasRef)}
        />
        {empty && <span className="absolute inset-0 flex items-center justify-center text-sm text-slate-300 pointer-events-none">Signez ici</span>}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={clear} className="text-slate-500" data-testid="signature-clear"><Eraser className="h-3.5 w-3.5 mr-1.5" />Effacer</Button>
        <Button type="button" variant="outline" size="sm" onClick={openFs} className="text-brand-700 border-brand-200" data-testid="signature-fullscreen"><Maximize2 className="h-3.5 w-3.5 mr-1.5" />Plein écran</Button>
      </div>

      {fs && createPortal((
        <div className="fixed inset-0 z-[100] bg-white flex flex-col" data-testid="signature-fullscreen-overlay">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
            <span className="font-medium text-slate-700">Signez ci-dessous</span>
            <Button type="button" variant="ghost" size="icon" onClick={() => setFs(false)} data-testid="signature-fs-close"><X className="h-5 w-5" /></Button>
          </div>
          <div className="flex-1 relative touch-none">
            <canvas
              ref={fsCanvasRef} width={1000} height={1200} data-testid="signature-canvas-fullscreen"
              className="w-full h-full touch-none cursor-crosshair bg-white"
              {...bind(fsCanvasRef)}
            />
            {empty && <span className="absolute inset-0 flex items-center justify-center text-lg text-slate-300 pointer-events-none">Signez ici avec votre doigt</span>}
          </div>
          <div className="flex items-center gap-3 px-4 py-3 border-t bg-slate-50">
            <Button type="button" variant="outline" onClick={clear} className="flex-1" data-testid="signature-fs-clear"><Eraser className="h-4 w-4 mr-2" />Effacer</Button>
            <Button type="button" onClick={validateFs} className="flex-1 bg-brand-600 hover:bg-brand-700 text-white" data-testid="signature-fs-validate"><Check className="h-4 w-4 mr-2" />Valider</Button>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
